import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer id="footer" data-testid="footer" className="relative bg-[#112A46] text-[#FAFAF7] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C48E66] mb-8">Villa d'Avó Dalva · Vila Chã · Vila do Conde</p>
        <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight max-w-4xl" data-testid="footer-tagline">
          {t.footer.tagline_1}{" "}
          <span className="italic text-[#F0EBE1]">{t.footer.tagline_2}</span>
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6" />
          <div className="md:col-span-6 md:text-right">
            <a href="#contact" data-testid="footer-cta"
              className="inline-block border border-[#C48E66] text-[#C48E66] px-10 py-5 text-[11px] uppercase tracking-[0.25em] hover:bg-[#C48E66] hover:text-[#112A46] transition-all duration-300">
              {t.footer.cta}
            </a>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-[11px] uppercase tracking-[0.25em] text-[#FAFAF7]/50">
          <div>© {year} Villa d'Avó Dalva · {t.footer.rights}</div>
          <div className="flex gap-8">
            <PrivacyDialog label={t.footer.privacy} />
            <GuestDialog label={t.footer.guest} />
          </div>
        </div>
      </div>
    </footer>
  );
}

function PrivacyDialog({ label }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="footer-privacy-btn" className="hover:text-[#C48E66] transition-colors">{label}</button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-[#FAFAF7] border-[#E2DFD2]">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#112A46] font-light">Política de Privacidade</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 text-sm text-[#5C665F] max-h-[60vh] overflow-y-auto pr-2">
          <p>A Villa d'Avó Dalva fará um uso responsável, leal e transparente dos dados pessoais recolhidos neste site, em conformidade com o Regulamento (UE) 2016/679 (RGPD). Quando os seus dados pessoais são recolhidos, armazenados, usados, transferidos ou excluídos por outras entidades que os tratam sob nosso comando (subcontratados), exigimos que também apresentem um grau igual de proteção e segurança.</p>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Informações que recolhemos</h4><p>Só recolhemos dados pessoais se existir uma solicitação de contacto neste site através do preenchimento do formulário para esse fim; nesse caso, recolhemos o nome, email e telefone. Estes são obrigatórios e necessários para responder à sua solicitação.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Como tratamos os seus dados</h4><p>Os dados recolhidos são utilizados exclusivamente para responder ao seu pedido, com base no consentimento que fornece ao preencher o questionário e submeter o formulário.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Período de retenção</h4><p>Os dados são conservados apenas pelo período necessário. Após esse período, são anonimizados ou eliminados.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Os seus direitos</h4><p>Pode exercer direitos de acesso, rectificação, eliminação e portabilidade dos dados. Contacte-nos por email para qualquer solicitação.</p></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GuestDialog({ label }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="footer-guest-btn" className="hover:text-[#C48E66] transition-colors">{label}</button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-[#FAFAF7] border-[#E2DFD2]">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#112A46] font-light">Informações Úteis</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 text-sm text-[#5C665F] max-h-[60vh] overflow-y-auto pr-2">
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Check-in e Check-out</h4><p>Check-in a partir das 15h00. Check-out até às 11h00. Para chegadas fora deste horário, contacte-nos antecipadamente.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Pagamento e Reserva</h4><p>Aceitamos pagamento por transferência bancária e MBWay. Pode ser solicitado um depósito para garantir a reserva.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Crianças</h4><p>Todas as crianças são bem-vindas. Berços e camas extra estão sujeitos a disponibilidade.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Período de Descanso</h4><p>Pedimos que seja respeitado o período entre as 00h00 e as 08h00, em consideração pelos vizinhos e outros hóspedes.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Animais de Estimação</h4><p>Não são permitidos animais de estimação nos alojamentos.</p></div>
          <div><h4 className="font-serif text-[#112A46] text-lg mb-2">Proibições</h4><p>É proibido fumar no interior. Não são permitidas festas ou eventos sem autorização prévia.</p></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
